import type { ProductLicenseSummary } from "../../../services/product_licenses.service";
import type { AssessmentTaskRequirementRef } from "../../../services/assessment_tasks.types";
import { workItemIdentity } from "../../../services/assessment_work_items.types";
import type { AssessmentWorkItem, AssessmentWorkItemListFilters } from "../../../services/assessment_work_items.types";
import type { EvidenceRequirementInvalidation } from "../../../services/evidence_requirement_invalidation.service";

export const FOOTER_PAGE_SIZE = 5;
export type TaskAssignmentsFooterScope = "assessment" | "requirement";

const positiveId = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

export function validFooterRequirementKey(value: unknown): value is string {
    if (typeof value !== "string" || !value || value.trim() !== value ||
        value.length > 510 || /[\x00-\x1f\x7f]/.test(value)) return false;
    let characters = 0;
    for (const character of value) {
        const codePoint = character.codePointAt(0)!;
        if ((codePoint >= 0xd800 && codePoint <= 0xdfff) || ++characters > 255) return false;
    }
    return true;
}

export function footerWorkItemFilters(
    scope: TaskAssignmentsFooterScope,
    productLicenseId: number,
    requirement: AssessmentTaskRequirementRef | null,
    page: number
): AssessmentWorkItemListFilters | null {
    if (!positiveId(productLicenseId) || !positiveId(page)) return null;
    const sharedFilters = {
        assessmentIds: [productLicenseId],
        page,
        pageSize: FOOTER_PAGE_SIZE
    };
    if (scope === "assessment") return { ...sharedFilters, view: "assessment_footer" };
    if (!requirement || !validFooterRequirementKey(requirement.sectionKey) ||
        !validFooterRequirementKey(requirement.requirementKey)) return null;
    return {
        ...sharedFilters,
        view: "requirement_footer",
        requirement: {
            sectionKey: requirement.sectionKey,
            requirementKey: requirement.requirementKey
        }
    };
}

interface FooterSource {
    productLicenseId: number;
    requirements: readonly AssessmentTaskRequirementRef[];
}

function matchesFooterRequirement(
    source: FooterSource,
    productLicenseId: number,
    requirement: AssessmentTaskRequirementRef
): boolean {
    return source.productLicenseId === productLicenseId && source.requirements.some((mapped) =>
        mapped.sectionKey === requirement.sectionKey &&
        mapped.requirementKey === requirement.requirementKey);
}

// The API owns authorization and total; this preserves only rows in the requested scope.
export function scopeFooterWorkItems(
    items: readonly AssessmentWorkItem[],
    productLicenseId: number,
    requirement: AssessmentTaskRequirementRef | null
): AssessmentWorkItem[] {
    if (!positiveId(productLicenseId) || (requirement &&
        (!validFooterRequirementKey(requirement.sectionKey) ||
            !validFooterRequirementKey(requirement.requirementKey)))) return [];
    const seen = new Set<string>();
    return items.filter((item) => {
        const identity = workItemIdentity(item);
        const source = item.kind === "task" ? item.task : item.evidence;
        if (seen.has(identity) || source.productLicenseId !== productLicenseId ||
            (requirement && !matchesFooterRequirement(source, productLicenseId, requirement))) return false;
        seen.add(identity);
        return true;
    });
}

function comparisonRequirementKey(value: string): string {
    // Match the server's persisted-key normalization; the signal has no section key.
    return value.trim().toUpperCase().replace(/_/g, ".");
}

export function evidenceChangeAffectsFooter(
    change: EvidenceRequirementInvalidation,
    accountId: number | null,
    productLicenseId: number,
    requirement: AssessmentTaskRequirementRef | null
): boolean {
    if (!positiveId(accountId) || !positiveId(productLicenseId) ||
        change.accountId !== accountId || change.productLicenseId !== productLicenseId) return false;
    if (!requirement || change.items === null) return true;
    const target = comparisonRequirementKey(requirement.requirementKey);
    return change.items.some((item) => {
        const scopes = change.kind === "create" ? [item.after]
            : change.kind === "delete" ? [item.before] : [item.before, item.after];
        return scopes.some((scope) => scope === null || scope.requirementKeys === null ||
            scope.requirementKeys.some((key) => comparisonRequirementKey(key) === target));
    });
}

export function canShowTaskAssignmentsFooter(
    license: ProductLicenseSummary | null,
    productLicenseId: number,
    userId: number | null,
    isAccountAdmin: boolean
): boolean {
    if (!license || license.id !== productLicenseId || !positiveId(userId)) return false;
    return isAccountAdmin || license.assignedIsaUserIdFromAssignments === userId ||
        license.assignedQsaUserIdFromAssignments === userId ||
        license.assignedQaUserIdFromAssignments === userId ||
        license.editorIds.includes(userId);
}
